import React, { useEffect, useState } from "react";
import AppointmentTimePicker from "../../Pages/eplanner/appointments/AppointmentTimePicker";
import "../../Pages/eplanner/appointments/AppointmentTimePicker.scss";
import "./AqPickers.scss";

function pad2(value) {
  return String(value).padStart(2, "0");
}

function digitsOnly(raw, maxLen) {
  return String(raw || "")
    .replace(/\D/g, "")
    .slice(0, maxLen);
}

/** Hour 0–23 while typing (24-hour clock). */
function sanitizeHour24Input(raw) {
  let h = digitsOnly(raw, 2);
  if (!h) return "";
  if (h.length === 1) {
    const n = Number(h);
    if (n > 2) return String(n); // 3–9 ok as single digit
    return h;
  }
  let n = Number(h);
  if (n > 23) return "23";
  return h;
}

/** Minute 0–59 while typing. */
function sanitizeMinuteInput(raw) {
  let m = digitsOnly(raw, 2);
  if (!m) return "";
  if (m.length === 1) {
    const n = Number(m);
    if (n > 5) return String(n);
    return m;
  }
  let n = Number(m);
  if (n > 59) return "59";
  return m;
}

function splitHHmm(hhmm) {
  const text = String(hhmm || "").trim();
  const match = text.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return { hour: "", minute: "" };
  const hour24 = Number(match[1]);
  const minute = Number(match[2]);
  if (Number.isNaN(hour24) || Number.isNaN(minute)) {
    return { hour: "", minute: "" };
  }
  return {
    hour: String(Math.min(23, Math.max(0, hour24))),
    minute: pad2(Math.min(59, Math.max(0, minute))),
  };
}

function toHHmm(hourRaw, minuteRaw) {
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return "";
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return "";
  return `${pad2(hour)}:${pad2(minute)}`;
}

function snapMinute(raw, intervalMinutes = 1) {
  const minute = Number(raw);
  if (Number.isNaN(minute)) return "";
  const step = Math.max(1, Number(intervalMinutes) || 1);
  if (step <= 1) return pad2(Math.min(59, Math.max(0, minute)));
  const snapped = Math.min(
    60 - step,
    Math.max(0, Math.round(minute / step) * step),
  );
  return pad2(snapped);
}

/**
 * AQ segmented time field — 24-hour HH:mm (clinical default) + clock panel.
 * Exact minutes by default (intervalMinutes=1); set higher to snap.
 */
export default function SegmentTimePicker({
  id,
  value = "",
  onChange,
  disabled = false,
  invalid = false,
  intervalMinutes = 1,
  "aria-label": ariaLabel = "Select time",
}) {
  const parts = splitHHmm(value);
  const [hour, setHour] = useState(parts.hour);
  const [minute, setMinute] = useState(parts.minute);

  useEffect(() => {
    const next = splitHHmm(value);
    setHour(next.hour);
    setMinute(next.minute);
  }, [value]);

  const commit = (nextHour, nextMinute) => {
    const normalized = toHHmm(nextHour, nextMinute);
    if (normalized) onChange?.(normalized);
  };

  const revertToValue = () => {
    const next = splitHHmm(value);
    setHour(next.hour);
    setMinute(next.minute);
  };

  return (
    <div className="aq-picker-wrap aq-picker-wrap--time">
      <AppointmentTimePicker
        value={value}
        onChange={onChange}
        disabled={disabled}
        intervalMinutes={intervalMinutes}
        ignorePast
        fullDay
        aria-label={ariaLabel}
        renderTrigger={({
          triggerRef,
          open,
          setOpen,
          disabled: pickerDisabled,
        }) => (
          <div
            ref={triggerRef}
            className={`aq-time-segments${open ? " is-open" : ""}${
              invalid ? " is-invalid" : ""
            }${pickerDisabled ? " is-disabled" : ""}`}
          >
            <input
              id={id}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              className="aq-time-segments__part"
              placeholder="--"
              maxLength={2}
              disabled={pickerDisabled}
              value={hour}
              aria-label="Hours (24-hour)"
              onChange={(e) => setHour(sanitizeHour24Input(e.target.value))}
              onBlur={() => {
                if (hour === "") {
                  revertToValue();
                  return;
                }
                const nextHour = pad2(
                  Math.min(23, Math.max(0, Number(hour) || 0)),
                );
                setHour(String(Number(nextHour)));
                const nextMinute =
                  minute === ""
                    ? "00"
                    : snapMinute(minute, intervalMinutes) || "00";
                setMinute(nextMinute);
                commit(nextHour, nextMinute);
              }}
            />
            <span className="aq-time-segments__sep">:</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              className="aq-time-segments__part"
              placeholder="--"
              maxLength={2}
              disabled={pickerDisabled}
              value={minute}
              aria-label="Minutes"
              onChange={(e) => setMinute(sanitizeMinuteInput(e.target.value))}
              onBlur={() => {
                if (minute === "") {
                  revertToValue();
                  return;
                }
                const nextMinute = snapMinute(minute, intervalMinutes);
                if (!nextMinute) {
                  revertToValue();
                  return;
                }
                setMinute(nextMinute);
                const nextHour =
                  hour === ""
                    ? "00"
                    : pad2(Math.min(23, Math.max(0, Number(hour) || 0)));
                setHour(String(Number(nextHour)));
                commit(nextHour, nextMinute);
              }}
            />
            <button
              type="button"
              className="aq-time-segments__clock"
              disabled={pickerDisabled}
              aria-label="Open time picker"
              aria-expanded={open}
              onClick={() => {
                if (pickerDisabled) return;
                setOpen((current) => !current);
              }}
            >
              <i className="far fa-clock" aria-hidden="true" />
            </button>
          </div>
        )}
      />
    </div>
  );
}
